import { Avatar } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import DataTable from "react-data-table-component";
import { useSelector, useDispatch } from "react-redux";

import ModalPopUp from "@/Components/common/ModalPopUp";
import { RejectIcon } from "@/Components/Icons/RejectIcon";
import { getColumnNormalHtml } from "@/utilities/datatable/columnFunctions";
import useDebounceScroll from "@/utilities/hooks/useDebounceScroll";

import { SearchFilter } from "./SearchFilter";
import {
  fetchBankShortlistCandidates,
  setBankShortListCandidatesLoading,
  toggleInvitedDirectBankCandidateForContractModal,
  updateSelectedShortListCandidateIds,
} from "../../../../reducers/clientcreatejob";
import { getComplianceClass } from "../../../../utilities/utils";
import { Miles } from "../../../common/client/candidates/Listing/common/Miles";
import CandidateStatusAndInfo from "../../../common/client/dataTables/CandidateStatusAndInfo";
import BlurredText from "../../../common/client/managedService/BlurredText";
import TableLoader from "../../../common/TableLoader";
import TableNoContent from "../../../common/TableNoContent";

const BookedInvitedBankCandidateForContractModal = () => {
  const {
    invitedBankCandidateForContractModal,
    shortlistCandidateData,
    isShortListLoading,
    totalCount,
    selectedCandidateIds,
  } = useSelector((state) => state.clientCreateJob);
  const { clientUserAllPermissions } = useSelector((state) => state.session);
  const [data, setData] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(toggleInvitedDirectBankCandidateForContractModal(false));
    };
  }, []);

  useEffect(() => {
    if (invitedBankCandidateForContractModal) {
      dispatch(fetchBankShortlistCandidates({}));
      dispatch(setBankShortListCandidatesLoading(true));
    }
  }, [invitedBankCandidateForContractModal]);

  useEffect(() => {
    setData(shortlistCandidateData);
  }, [shortlistCandidateData]);

  const handleSave = () => {
    dispatch(toggleInvitedDirectBankCandidateForContractModal(false));
  };
  const handleClose = () => {
    dispatch(toggleInvitedDirectBankCandidateForContractModal(false));
    // dispatch(deSelectShortListAllCandidate())
  };

  const columns = [
    {
      name: "Candidate",
      sortField: "candidate_name",
      selector: (row) => (
        <div className="flex gap-[14px]">
          <div className="min-w-[45px]">
            <Avatar
              alt={row.candidate_name
                .trim()
                .split(" ")[0]
                .charAt(0)
                .toUpperCase()}
              sx={{ width: 45, height: 45 }}
              src={row.profile_image ? row.profile_image : ""}
            />
          </div>
          <div className="flex grow flex-col gap-1 overflow-hidden">
            {row.route_id == "-" ? (
              <span className="truncate text-[14px] font-semibold capitalize leading-[normal] text-black">
                {row.candidate_name}
              </span>
            ) : !clientUserAllPermissions ||
              clientUserAllPermissions.is_allow_to_view_candidate_profile ===
                1 ? (
              <>
                <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                  {row.candidate_name}
                </span>
              </>
            ) : (
              <span className="cursor-default truncate text-[14px] font-semibold leading-[normal] text-black">
                {row.candidate_name}
              </span>
            )}
            <CandidateStatusAndInfo
              sortlisted={row.candidate_sortlisted}
              unsuitable={row.candidate_unsuitable}
              extraEle={
                row.candidate_ref_no && row.candidate_ref_no !== "-" ? (
                  <span className="text-[12px] font-medium leading-[normal] text-txtColor">
                    {row.candidate_ref_no}
                  </span>
                ) : null
              }
            />
          </div>
        </div>
      ),
      width: "20.5%",
    },
    {
      name: "Contact",
      sortField: "candidate_email",
      selector: (row) => (
        <div className="flex flex-col">
          <BlurredText type="email">
            {row.mobile_phone_number && row.mobile_phone_number !== "-"
              ? getColumnNormalHtml(row.mobile_phone_number || "-")
              : null}
          </BlurredText>
          <BlurredText type="phone">
            <span className="line-clamp-1 text-[12px] font-medium leading-[normal] text-txtColor">
              {row.candidate_email}
            </span>
          </BlurredText>
        </div>
      ),
      width: "18%",
    },

    {
      name: "Role",
      sortField: "role_name",
      selector: (row) => (
        <div className="flex flex-col">
          {getColumnNormalHtml(
            row.role_name,
            "line-clamp-2 whitespace-break-spaces",
          )}
          {row.license_number && row.license_number !== "-" ? (
            <span className="text-[12px] font-medium leading-[normal] text-txtColor">
              {row.license_number}
            </span>
          ) : null}
        </div>
      ),
      width: "16%",
    },
    {
      name: "Address",
      sortField: "candidate_address",
      selector: (row) => (
        <div className="flex flex-col items-start">
          {row.miles && row.miles !== "-" ? <Miles miles={row.miles} /> : null}
          <BlurredText type="address">
            {getColumnNormalHtml(
              row.candidate_address,
              "line-clamp-2 whitespace-break-spaces truncate",
            )}
          </BlurredText>
        </div>
      ),
      width: "20%",
    },
    {
      name: "Compliance",
      selector: (row) => (
        <div className="flex flex-col gap-1">
          {row.profile_percent ? (
            <span
              className={`leading-[ h-[23px] w-[75px] rounded-[4px] text-center text-[14px] font-medium ${getComplianceClass(
                row.profile_percent,
              )}`}
            >
              {row.profile_percent == "-" ? "0%" : row.profile_percent + "%"}
            </span>
          ) : null}
        </div>
      ),
      width: "auto",
    },
    {
      name: "Total Hours",
      selector: (row) => (
        <div>{getColumnNormalHtml(row.candidate_total_worked)}</div>
      ),
      width: "auto",
    },
  ];

  const dataTableRef = useRef();

  const handleDebounceScroll = useDebounceScroll(() =>
    dispatch(fetchBankShortlistCandidates({})),
  );
  useEffect(() => {
    if (dataTableRef.current) {
      const table = dataTableRef.current.querySelector("._DataTable");
      table.addEventListener("scroll", handleDebounceScroll);
      return () => {
        table.removeEventListener("scroll", handleDebounceScroll);
      };
    }
  }, [handleDebounceScroll]);

  return (
    <ModalPopUp
      show={invitedBankCandidateForContractModal}
      panelClass="max-w-[80vw] max-sm:max-w-[400px]"
      onClose={() => {
        handleClose(true);
      }}
    >
      <div className="EditUserModal_wrapper flex flex-col gap-4">
        <div className="flex justify-between">
          <div className="flex items-center justify-center gap-5">
            <h3 className="text-[20px] font-semibold leading-[normal] text-black">
              Shortlist candidate
            </h3>
          </div>
          <div className="flex flex-wrap items-center gap-5">
            <span className="text-center text-[16px] font-medium leading-[normal] text-txtColor">
              {!isShortListLoading ? (
                <>
                  Total Candidates : <b className="text-black">{totalCount}</b>
                </>
              ) : null}
            </span>
            <SearchFilter />

            <button
              onClick={handleClose}
              className="text-red outline-none ring-0"
            >
              <RejectIcon />
            </button>
          </div>
        </div>

        <div className="DataTable_Wrapper" ref={dataTableRef}>
          <DataTable
            columns={columns}
            data={data}
            fixedHeader
            fixedHeaderScrollHeight="500px"
            progressPending={isShortListLoading}
            noDataComponent={<TableNoContent />}
            // clearSelectedRows={toggleSelected}
            progressComponent={<TableLoader rows={5} />}
            selectableRows
            // selectableRowsSingle
            // onRowClicked={(state) => {
            //   // console.log("row", row)
            //   if (state.candidate_id == "-") {
            //     showErrorToast("Action not allowed : You can't select unregistered candidate", {
            //       id: "admin-selected",
            //     });
            //   }

            // }}
            customStyles={{
              rows: {
                style: {
                  '& .rdt_TableCell input[type="checkbox"]:disabled ': {
                    border: "2px solid lightGrey !important", // Applies the grey border for admin's or own row checkbox
                  },
                },
              },
            }}
            selectableRowSelected={(row) =>
              selectedCandidateIds.includes(row.candidate_id)
            }
            onSelectedRowsChange={(state) => {
              let selectedCandidateId = state.selectedRows.map((item) => {
                return item.candidate_id;
              });
              dispatch(
                updateSelectedShortListCandidateIds(selectedCandidateId),
              );
            }}
            className="_DataTable"
            selectableRowDisabled={(row) => row.candidate_id == "-"}
          />
        </div>

        <div className="mt-5 flex justify-end gap-4">
          <button
            className="transition-linear relative h-[45px] w-[90px] rounded-lg border border-primary bg-primary text-[14px] font-medium text-white hover:bg-none hover:text-primary"
            onClick={handleSave}
          >
            Save
          </button>
          <button
            className="transition-linear relative h-[45px] w-[80px] rounded-lg border border-primary bg-white text-[14px] font-medium text-blue hover:bg-blue hover:text-white"
            onClick={handleClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalPopUp>
  );
};

export default BookedInvitedBankCandidateForContractModal;
